import { useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

export default function MutasiMasukLaporan() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [filter, setFilter] = useState({
    startDate: "",
    endDate: "",
  });

  const isFilterReady = filter.startDate && filter.endDate;

  // =========================
  // FETCH DATA
  // =========================
  const fetchData = async () => {
    try {
      if (!isFilterReady) return;

      setLoading(true);

      const kode_toko = localStorage.getItem("kode_toko");

      const res = await axios.get("/api/warehouse/inbound-mutation-report", {
        params: {
          start_date: filter.startDate,
          end_date: filter.endDate,
          kode_toko,
        },
      });

      setData(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // SEARCH FILTER
  // =========================
  const filteredData = useMemo(() => {
    if (!search) return data;

    return data.filter((d) =>
      `
      ${d.kode_extend}
      ${d.ukuran}
      ${d.id_outbound_gudang_ho}
      ${d.uniq_id}
      ${d.barcode}
      `
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [data, search]);

  // =========================
  // SUMMARY
  // =========================
  const summary = useMemo(() => {
    return {
      total_sj: new Set(data.map((d) => d.id_outbound_gudang_ho)).size,
      total_artikel: new Set(data.map((d) => d.kode_extend)).size,
      total_qty: data.reduce((a, b) => a + Number(b.quantity || 0), 0),
      total_hjp: data.reduce(
        (a, b) =>
          a + Number(b.quantity || 0) * Number(b.hjp || 0),
        0
      ),
    };
  }, [data]);

  // =========================
  // GROUPING BARCODE + SIZE
  // =========================
  const groupedBarcodeSize = useMemo(() => {
    const map = {};

    data.forEach((item) => {
      const key = `${item.barcode}_${item.ukuran}`;

      if (!map[key]) {
        map[key] = {
          artikel: item.kode_extend,
          barcode: item.barcode,
          ukuran: item.ukuran,
          brand: item.prinsipal,
          dept: item.dept,
          qty: 0,
          total_hjp: 0,
        };
      }

      map[key].qty += Number(item.quantity || 0);

      map[key].total_hjp +=
        Number(item.quantity || 0) *
        Number(item.hjp || 0);
    });

    return Object.values(map);
  }, [data]);

  // =========================
  // EXPORT EXCEL
  // =========================
  const exportExcel = () => {
    const wb = XLSX.utils.book_new();

    // ================= RAW DATA =================
   const rawData = data.map((d) => ({
  "SJ ID": d.id_outbound_gudang_ho,
  TANGGAL_SCAN_PENERIMAAN: new Date(d.tanggal)
    .toISOString()
    .replace("T", " ")
    .replace("Z", ""),
  "UNIQ ID": d.uniq_id,
  ARTIKEL: d.kode_extend,
  BARCODE: d.barcode,
  BRAND: d.prinsipal,
  DEPT: d.dept,
  SIZE: d.ukuran,
  QTY: Number(d.quantity),
  HJP: parseFloat(d.hjp) || 0,
}));

    const wsRaw = XLSX.utils.json_to_sheet(rawData);

    wsRaw["!cols"] = [
      { wch: 12 },
      { wch: 18 },
      { wch: 18 },
      { wch: 22 },
      { wch: 16 },
      { wch: 14 },
      { wch: 10 },
      { wch: 10 },
      { wch: 15 },
      { wch: 14 },
    ];

    // ================= GROUP BARCODE =================
    const barcodeSheet = groupedBarcodeSize.map((d) => ({
      ARTIKEL: d.artikel,
      BARCODE: d.barcode,
      BRAND: d.brand,
      DEPT: d.dept,
      SIZE: d.ukuran,
      QTY: d.qty,
      "TOTAL HJP": d.total_hjp,
    }));

    const wsBarcode = XLSX.utils.json_to_sheet(barcodeSheet);

    wsBarcode["!cols"] = [
      { wch: 18 },
      { wch: 22 },
      { wch: 16 },
      { wch: 14 },
      { wch: 10 },
      { wch: 10 },
      { wch: 18 },
    ];

    // ================= SUMMARY =================
    const summarySheet = [
      {
        METRIC: "TOTAL SJ",
        VALUE: summary.total_sj,
      },
      {
        METRIC: "TOTAL ARTIKEL",
        VALUE: summary.total_artikel,
      },
      {
        METRIC: "TOTAL QTY",
        VALUE: summary.total_qty,
      },
      {
        METRIC: "TOTAL HJP",
        VALUE: summary.total_hjp,
      },
    ];

    const wsSummary = XLSX.utils.json_to_sheet(summarySheet);

    wsSummary["!cols"] = [
      { wch: 25 },
      { wch: 20 },
    ];

    // ================= APPEND =================
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");
    XLSX.utils.book_append_sheet(wb, wsBarcode, "Grouping Barcode");
    XLSX.utils.book_append_sheet(wb, wsRaw, "Raw Data");

    XLSX.writeFile(
      wb,
      `inbound-mutation-report-${filter.startDate}-${filter.endDate}.xlsx`
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen space-y-4">

      {/* HEADER */}
      <div>
        <h1 className="text-xl font-bold">
          Inbound Mutation Report
        </h1>

        <p className="text-sm text-gray-500">
          Datatable + summary + export excel grouping barcode
        </p>
      </div>

      {/* FILTER */}
      <div className="bg-white border rounded-xl p-4 flex gap-3 items-end">

        <input
          type="date"
          className="border rounded-lg px-3 py-2 text-sm"
          value={filter.startDate}
          onChange={(e) =>
            setFilter({
              ...filter,
              startDate: e.target.value,
            })
          }
        />

        <input
          type="date"
          className="border rounded-lg px-3 py-2 text-sm"
          value={filter.endDate}
          onChange={(e) =>
            setFilter({
              ...filter,
              endDate: e.target.value,
            })
          }
        />

        <button
          onClick={fetchData}
          disabled={!isFilterReady}
          className={`px-4 py-2 rounded-lg text-sm text-white
          ${isFilterReady ? "bg-black" : "bg-gray-300"}
          `}
        >
          Load Data
        </button>

        <input
          className="ml-auto border px-3 py-2 rounded-lg text-sm w-72"
          placeholder="Search SKU / Barcode / UID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <button
          onClick={exportExcel}
          disabled={!data.length}
          className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm"
        >
          Export Excel
        </button>
      </div>

      {/* SUMMARY */}
      {isFilterReady && (
        <div className="grid grid-cols-4 gap-3">

          <div className="bg-white border rounded-xl p-4">
            <div className="text-xs text-gray-500">
              Total SJ
            </div>

            <div className="text-xl font-semibold">
              {summary.total_sj}
            </div>
          </div>

          <div className="bg-white border rounded-xl p-4">
            <div className="text-xs text-gray-500">
              Total Artikel
            </div>

            <div className="text-xl font-semibold">
              {summary.total_artikel}
            </div>
          </div>

          <div className="bg-white border rounded-xl p-4">
            <div className="text-xs text-gray-500">
              Total Qty
            </div>

            <div className="text-xl font-semibold">
              {summary.total_qty}
            </div>
          </div>

          <div className="bg-white border rounded-xl p-4">
            <div className="text-xs text-gray-500">
              Total HJP
            </div>

            <div className="text-xl font-semibold">
              Rp {summary.total_hjp.toLocaleString("id-ID")}
            </div>
          </div>

        </div>
      )}

      {/* TABLE */}
      {isFilterReady && (
        <div className="bg-white border rounded-xl overflow-hidden">

          <div className="p-4 border-b font-semibold">
            Inbound Datatable
          </div>

          <div className="overflow-auto max-h-[650px]">

            <table className="w-full text-sm">

              <thead className="bg-gray-100 sticky top-0 z-10">
                <tr className="text-left text-xs">

                  <th className="p-2">Photo</th>
                   <th className="p-2">Tanggal Posting</th>
                  <th className="p-2">SJ ID</th>
                  <th className="p-2">Uniq ID</th>
                  <th className="p-2">Artikel</th>
                  <th className="p-2">Barcode</th>
                  <th className="p-2">Brand</th>
                  <th className="p-2">Dept</th>
                  <th className="p-2">Size</th>
                  <th className="p-2">Qty</th>
                  <th className="p-2">HJP</th>
                 

                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      className="p-6 text-center"
                      colSpan={11}
                    >
                      Loading...
                    </td>
                  </tr>
                ) : filteredData.length ? (
                  filteredData.map((d, i) => (
                    <tr
                      key={i}
                      className="border-t hover:bg-gray-50 transition"
                    >

                      <td className="p-2">
                        <img
                          src={d.photo}
                          className="w-10 h-10 rounded-md object-cover border"
                        />
                      </td>
                       <td className="p-2">
                        {d.tanggal
                          ? new Date(d.tanggal)
    .toISOString()
    .replace("T", " ")
    .replace("Z", "")
                          : "-"}
                      </td>

                      <td className="p-2">
                        {d.id_outbound_gudang_ho}
                      </td>

                      <td className="p-2 font-medium text-purple-600">
                        {d.uniq_id}
                      </td>

                      <td className="p-2 font-medium text-blue-600">
                        {d.kode_extend}
                      </td>

                      <td className="p-2">
                        {d.barcode}
                      </td>

                      <td className="p-2">
                        {d.prinsipal}
                      </td>

                      <td className="p-2">
                        {d.dept}
                      </td>

                      <td className="p-2">
                        {d.ukuran}
                      </td>

                      <td className="p-2 font-semibold">
                        {d.quantity}
                      </td>

                      <td className="p-2">
                        Rp{" "}
                        {Number(d.hjp || 0).toLocaleString(
                          "id-ID"
                        )}
                      </td>

                     

                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      className="p-6 text-center text-gray-400"
                      colSpan={11}
                    >
                      Tidak ada data
                    </td>
                  </tr>
                )}
              </tbody>

            </table>

          </div>

        </div>
      )}

    </div>
  );
}